import { useEffect, useState } from "react";
import {
  resolveEmulatorAddress,
  useEmulator,
} from "~/integrations/firebase/app";

interface OobEntry {
  email: string;
  requestType: string;
  oobCode: string;
}

async function fetchEmulatorLoginLink(
  targetEmail: string,
): Promise<string | null> {
  const { host, port } = resolveEmulatorAddress("auth");
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  const tenantId = import.meta.env.VITE_FIREBASE_AUTH_TENANT_ID;
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
  const tenantSegment = tenantId ? `/tenants/${tenantId}` : "";

  const url = `http://${host}:${port}/emulator/v1/projects/${projectId}${tenantSegment}/oobCodes`;
  const response = await fetch(url);
  if (!response.ok) return null;

  const data = await response.json();
  const codes: OobEntry[] = (data.oobCodes ?? []).filter(
    (e: OobEntry) =>
      e.email === targetEmail && e.requestType === "EMAIL_SIGNIN",
  );
  if (codes.length === 0) return null;

  const { oobCode } = codes[codes.length - 1];
  const tenantParam = tenantId ? `&tenantId=${tenantId}` : "";
  return `/login-link?mode=signIn&oobCode=${oobCode}&apiKey=${apiKey}${tenantParam}`;
}

export function useEmulatorLoginLink(email: string, open: boolean) {
  const [loginLink, setLoginLink] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !useEmulator || !email) {
      // clears the link when the dialog is closed or inputs are missing; the populated value is fetched asynchronously later in the same effect
      setLoginLink(null);
      return;
    }

    let cancelled = false;

    fetchEmulatorLoginLink(email).then((link) => {
      if (!cancelled) setLoginLink(link);
    });

    return () => {
      cancelled = true;
    };
  }, [email, open]);

  return loginLink;
}
